import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, Tabs } from "@mui/material";
import { styled } from "@mui/system";
import colorVariants from "color-variants";
import {
  setPageToShowIndex,
  setAllDepotList,
  setPageLoading,
  setDepotColourMap,
  setErrorModalMessage,
  setShowErrorModal,
  setActiveDatesForAllocationDatePicker,
  setOverlayTimeBand,
} from "../../../reducers/app";
import {
  allDepotListSelector,
  pageToShowIndexSelector,
} from "../../../reducers/app/selector";
import { setBoundariesForMultipleDepots } from "../../../reducers/depot";
import { setMapBounds } from "../../../reducers/map";
import { getAllDepotsWithMapboxBoundaries } from "../../../utils/api";
import { groupByPostcodeLevel } from "../../../utils/groupByPostcodeLevel";
import { SIDE_BAR_TABS as sideBarTabs } from "../../../constants/sideBarTabs";
import { DEFAULT_MAPBOUNDS } from "../../../constants/map";
import { depotColours } from "../../../styles/depots";
import { districtBoundariesSelector } from "../../../reducers/depot/selector";
import { getDepotBounds, filterListByBusinessUnit } from "../../../utils/map";
import { DEFAULT_TIMEBAND } from "../../../constants/timeBands";

const StyledTabs = styled(Tabs)({
    padding: "0 0.938rem",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    width: "100vw",
  '& .MuiTabs-indicator': {
    backgroundColor: "black",
  },
});

const StyledTab = styled(Tab)({
    minWidth: 0,
    color: "black",
    fontWeight: "400",
    fontSize: "15px",
    marginLeft: "0.2rem",
    marginRight: "0.2rem",
    lineHeight: "1.25rem",
    minHeight: "1.25rem",
    "&:focus": {
      opacity: 1,
    },
    '& .MuiTabs-selected': {
      pointerEvents: "none",
    },
})

export const SideBarTabs = () => {
  const tabIndex = useSelector(pageToShowIndexSelector);
  const allDepots = useSelector(allDepotListSelector);
  const districtBoundariesForOneDepot = useSelector(districtBoundariesSelector);

  const dispatch = useDispatch();

  const createDepotColourMap = (depotList) => {
    const depotsToAdd = depotList.map((depot, index) => {
      if (depotColours[index] === undefined) {
        index = index - depotColours.length;
      }
      const mapColour = depotColours[index];

      const mapColourVariations = colorVariants({
        base: mapColour,
        light: { steps: 4, lighten: 0.6, hueShift: 10, saturate: 0 },
        dark: { steps: 6, darken: 0.4, hueShift: -20, saturate: 1 },
      });
      // Array of colour variations -> steps mean how many of each variant [[...light colours], base colour, [...dark colours]]

      mapColourVariations.splice(4, 1);
      // Remove base colour

      return {
        depot: depot.depotCode,
        mapColour,
        altMapColour: mapColourVariations[2],
        mapColourVariations,
      };
    });

    return depotsToAdd;
  };

  return (
    <StyledTabs
      centered={true}
      onChange={async (e, value) => {
        dispatch(setPageToShowIndex(value));
        // Only need to call Depot api once
        if (value === 1 && allDepots.length < 1) {
          dispatch(setPageLoading(true));
          try {
            const getDepots = await getAllDepotsWithMapboxBoundaries();
            const getAllDepots = getDepots.data.depots;
            const getMultipleDepotBoundaries = getDepots.data.boundaries;
            const getActiveDates = getDepots.data.overlayDates;
            const groupedBoundaries = groupByPostcodeLevel(
              getMultipleDepotBoundaries
            );
            dispatch(setAllDepotList(getAllDepots));

            dispatch(setActiveDatesForAllocationDatePicker(getActiveDates));
            dispatch(setDepotColourMap(createDepotColourMap(getAllDepots)));
            const sectorBoundaries = filterListByBusinessUnit(
              groupedBoundaries.sectorBoundaries,
              1
            );
            const districtBoundaries = filterListByBusinessUnit(
              groupedBoundaries.districtBoundaries,
              1
            );
            dispatch(
              setBoundariesForMultipleDepots({
                sectorBoundaries,
                districtBoundaries,
                allBoundaries: groupedBoundaries,
              })
            );
            dispatch(setMapBounds(DEFAULT_MAPBOUNDS));
          } catch (e) {
            dispatch(setErrorModalMessage("Getting Depots For Full Map"));
            dispatch(setShowErrorModal(true));
          }
          dispatch(setPageLoading(false));
        } else if (value === 0 && districtBoundariesForOneDepot.length > 0) {
          dispatch(setMapBounds(getDepotBounds(districtBoundariesForOneDepot)));
          dispatch(setOverlayTimeBand(DEFAULT_TIMEBAND));
        }
      }}
      value={tabIndex}
    >
      {sideBarTabs.map((tab, index) => {
        return (
          <StyledTab
            key={index}
            label={tab}
            value={index}
            disableRipple
            style={{
              minWidth: `${tab.length} rem`,
            }}
          />
        );
      })}
    </StyledTabs>
  );
};
